import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { PurpleButton } from 'Components/Button';

import { ValidateBackGround } from 'Components/Validation';
import DatePicker from 'react-datepicker';

import formClasses from 'Themes/form/form.module.css';
import classes from './createTimeCardModal.module.css';

import 'react-datepicker/dist/react-datepicker-min.module.css';

interface Props {
  // modal variables
  isOpen: boolean;
  canCreate: boolean;
  formErrors: any;
  // employee variables
  employees: any[];
  selectedEmployeeId: number;
  // projectVariables
  projects: any[];
  selectedProjectId: number;
  // date variables
  startDate: any;
  endDate: any;
  // modal functions
  modalCloseClick: (e: any) => void;
  onCreateButtonClick: (e: any) => void;
  // employee functions
  setSelectedEmployee: (e: any) => void;
  // project functions
  setSelectedProject: (e: any) => void;
  // date functions
  onChangeStartDate: (e: any) => void;
  onChangeEndDate: (e: any) => void;
}

const CreateTimeCardModal = ({
  isOpen,
  canCreate,
  employees,
  selectedEmployeeId,
  projects,
  selectedProjectId,
  formErrors,
  startDate,
  endDate,
  modalCloseClick,
  onCreateButtonClick,
  setSelectedEmployee,
  setSelectedProject,
  onChangeStartDate,
  onChangeEndDate,
}: Props) => (
  <Modal
    id="add-timecard-modal"
    classes={classes}
    title="Add Timecard"
    isOpen={isOpen}
    modalHeader
    dataBsBackdrop="static"
    dataBsKeyboard="false"
    modalCloseClick={modalCloseClick}
  >
    <form className={`requires-validation g-3 ${formClasses.formBase}`} noValidate action="#">
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.user_id?.length}>
          <Label ariaLabel="Employee" className={formClasses.label} htmlFor="employee">
            Employee
          </Label>
          <select
            className={classes.selectField}
            value={selectedEmployeeId || ''}
            onChange={(e) => setSelectedEmployee(Number(e.target.value))}
          >
            <option value="" disabled>Select Employee</option>
            {employees?.map((emp: any) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.user_id?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.user_id?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.project_id?.length}>
          <Label ariaLabel="Project" className={formClasses.label} htmlFor="project">
            Project
          </Label>
          <select
            className={classes.selectField}
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProject(Number(e.target.value))}
          >
            <option value="" disabled>Select Project</option>
            {projects?.map((proj: any) => (
              <option key={proj.id} value={proj.id}>
                {proj.name}
              </option>
            ))}
          </select>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.project_id?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.project_id?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.time_in?.length}>
          <Label ariaLabel="Clock In Time" className={formClasses.label} htmlFor="clockInTime">
            Clock In
          </Label>
          <div className={classes.dateTimeRow}>
            <DatePicker
              selected={startDate}
              onChange={onChangeStartDate}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className={classes.dateField}
              calendarClassName={classes.calendar}
            />
            <DatePicker
              selected={startDate}
              onChange={onChangeStartDate}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={classes.timeField}
            />
          </div>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.time_in?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.time_in?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.time_out?.length}>
          <Label ariaLabel="Clock Out Time" className={formClasses.label} htmlFor="clockOutTime">
            Clock Out
          </Label>
          <div className={classes.dateTimeRow}>
            <DatePicker
              selected={endDate}
              onChange={onChangeEndDate}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className={classes.dateField}
              calendarClassName={classes.calendar}
            />
            <DatePicker
              selected={endDate}
              onChange={onChangeEndDate}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={classes.timeField}
            />
          </div>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.time_out?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.time_out?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={`modal-footer ${classes.modalFooter}`}>
        <PurpleButton onClick={modalCloseClick} className={classes.cancelButton}>
          Cancel
        </PurpleButton>
        <PurpleButton onClick={onCreateButtonClick} disabled={!canCreate} className={classes.createButton}>
          Create
        </PurpleButton>
      </div>
    </form>
  </Modal>
);

const CreateTimeCardModalMemo = memo(CreateTimeCardModal, areEqual);

export { CreateTimeCardModalMemo as CreateTimeCardModal };
